import React, { useState, useEffect, useRef } from "react";
import {
  Mail,
  Lock,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Loader2,
  Fingerprint,
  ShieldCheck,
} from "lucide-react";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { t, useLanguage } from "@/app/utils/i18n";
import { supabase } from "@/utils/supabase/client";
import { useGlobal } from "@/app/providers/global-provider";
import { useAppTheme } from "@/app/providers/theme-provider";
import { UniversalAppHeader } from "./universal-app-header";
import { AqlSeahorseLogo, AqlVisionMark } from "./branding/aql-vision-brand";
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";

interface UniversalSmartLoginProps {
  apiUrl: string;
  publicAnonKey: string;
  appName: string;
  appId: string;
  appIcon: React.ReactNode;
  appGradient: string;
  onLoginSuccess: (token: string, email: string) => void;
  onBack?: () => void;
  fixedEmail?: string; // NEW: Pre-fill and auto-submit email
  requireClientSelection?: boolean;
}

type LoginStep =
  | "email"
  | "code"
  | "password"
  | "reset-request"
  | "reset-code"
  | "reset-password";

type LoginProfile = {
  id: string;
  name: string;
  role: string;
  roleLabel: string;
  badge: string;
  initials: string;
};

type MorphRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type PanelFlip = {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  active: boolean;
};

export function UniversalSmartLogin({
  apiUrl,
  publicAnonKey,
  appName,
  appId,
  appIcon,
  appGradient,
  onLoginSuccess,
  onBack,
  fixedEmail,
  requireClientSelection = false,
}: UniversalSmartLoginProps) {
  const language = useLanguage();
  const navigate = useNavigate();
  const { client, availableClients, setClient } = useGlobal();
  const { theme } = useAppTheme();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState(fixedEmail || "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [hasFaceId, setHasFaceId] = useState(false);
  const [isBiometricsSupported, setIsBiometricsSupported] =
    useState(false);
  const [isConditionalSupported, setIsConditionalSupported] =
    useState(false);
  const [loginProfiles, setLoginProfiles] = useState<LoginProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<LoginProfile | null>(null);
  const [clientQuery, setClientQuery] = useState("");
  const [isAqlEngineeringUser, setIsAqlEngineeringUser] = useState(() => {
    const storedEmail = String(localStorage.getItem("visionAlertsEmail") || localStorage.getItem("aql_user_email") || fixedEmail || "").trim().toLowerCase();
    return storedEmail.endsWith("@aqlengineering.com");
  });
  const [clientConfirmed, setClientConfirmed] = useState(() => localStorage.getItem(`aql_login_client_confirmed_${appId}`) === "1");
  const [pendingMorphFrom, setPendingMorphFrom] = useState<MorphRect | null>(null);
  const [panelFlip, setPanelFlip] = useState<PanelFlip | null>(null);
  const [slideDir, setSlideDir] = useState<"right" | "left">("right");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const profilePickerRef = useRef<HTMLDivElement | null>(null);
  const usesSupabaseAuth = appId === "vision-alerts";
  const activeClientId = client?.id ?? "";
  const hasConfirmedClient = clientConfirmed && Boolean(activeClientId);
  const normalizeClientKey = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const needsClientStep = usesSupabaseAuth && requireClientSelection && !fixedEmail && !hasConfirmedClient;

  useEffect(() => {
    if (!usesSupabaseAuth || !requireClientSelection || fixedEmail) return;
    if (!clientConfirmed || activeClientId) return;
    setClientConfirmed(false);
    localStorage.removeItem(`aql_login_client_confirmed_${appId}`);
  }, [activeClientId, appId, clientConfirmed, fixedEmail, requireClientSelection, usesSupabaseAuth]);

  useEffect(() => {
    setMounted(true);
    // Check if biometrics and conditional mediation are supported
    if (
      window.PublicKeyCredential &&
      window.PublicKeyCredential.isConditionalMediationAvailable
    ) {
      window.PublicKeyCredential.isConditionalMediationAvailable().then(
        (available) => {
          setIsConditionalSupported(available);
        },
      );
      setIsBiometricsSupported(true);
    }

    // Auto-submit if fixedEmail is provided and we are on email step
    if (
      fixedEmail &&
      step === "email" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fixedEmail)
    ) {
      const timer = setTimeout(() => {
        const fakeEvent = {
          preventDefault: () => {},
        } as React.FormEvent;
        handleEmailSubmit(fakeEvent);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fixedEmail]);

  useEffect(() => {
    if (!usesSupabaseAuth) return;

    let active = true;
    const resolveAqlEngineeringUser = async () => {
      const storedEmail = String(localStorage.getItem("visionAlertsEmail") || localStorage.getItem("aql_user_email") || fixedEmail || "").trim().toLowerCase();
      if (storedEmail.endsWith("@aqlengineering.com")) {
        setIsAqlEngineeringUser(true);
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (!active) return;
      const sessionEmail = String(data.session?.user?.email || "").trim().toLowerCase();
      setIsAqlEngineeringUser(sessionEmail.endsWith("@aqlengineering.com"));
    };

    resolveAqlEngineeringUser();
    return () => {
      active = false;
    };
  }, [fixedEmail, usesSupabaseAuth]);

  useEffect(() => {
    if (!usesSupabaseAuth || fixedEmail || needsClientStep || !activeClientId) {
      setLoginProfiles([]);
      setProfilesLoading(false);
      return;
    }

    let active = true;
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/vision-settings/login-profiles?client_id=${encodeURIComponent(activeClientId)}`,
          {
            headers: {
              Authorization: `Bearer ${publicAnonKey}`,
              apikey: publicAnonKey,
            },
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!active) return;
        const profiles = Array.isArray(data.profiles) ? data.profiles : [];
        setLoginProfiles(
          profiles
            .filter((profile: any) => profile?.id && profile?.name)
            .map((profile: any) => ({
              id: String(profile.id),
              name: String(profile.name),
              role: String(profile.role ?? "viewer"),
              roleLabel: String(profile.roleLabel ?? "Utilizador"),
              badge: String(profile.badge ?? profile.roleLabel ?? "Utilizador"),
              initials: String(profile.initials ?? "U").slice(0, 3),
            })),
        );
      } catch (err) {
        if (active) setLoginProfiles([]);
      } finally {
        if (active) setProfilesLoading(false);
      }
    };

    loadProfiles();
    return () => {
      active = false;
    };
  }, [activeClientId, apiUrl, fixedEmail, needsClientStep, publicAnonKey, usesSupabaseAuth]);

  useEffect(() => {
    if (!pendingMorphFrom || step !== "password" || !selectedProfile) return;

    const firstFrame = window.requestAnimationFrame(() => {
      const target = panelRef.current?.getBoundingClientRect();
      if (!target) {
        setPendingMorphFrom(null);
        return;
      }

      setPanelFlip({
        x: pendingMorphFrom.left - target.left,
        y: pendingMorphFrom.top - target.top,
        scaleX: pendingMorphFrom.width / target.width,
        scaleY: pendingMorphFrom.height / target.height,
        active: true,
      });

      const secondFrame = window.requestAnimationFrame(() => {
        setPanelFlip((current) => current ? { ...current, active: false } : null);
      });

      window.setTimeout(() => {
        setPanelFlip(null);
        setPendingMorphFrom(null);
      }, 460);

      return () => window.cancelAnimationFrame(secondFrame);
    });

    return () => window.cancelAnimationFrame(firstFrame);
  }, [pendingMorphFrom, selectedProfile, step]);

  // Check if user has Face ID registered when email is entered
  useEffect(() => {
    const checkFaceIdStatus = async () => {
      if (usesSupabaseAuth) {
        setHasFaceId(false);
        return;
      }
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        try {
          const response = await fetch(
            `${apiUrl}/auth/webauthn/status?email=${encodeURIComponent(email)}`,
            {
              headers: {
                Authorization: `Bearer ${publicAnonKey}`,
              },
            },
          );
          const data = await response.json();
          setHasFaceId(data.hasFaceId);
        } catch (err) {
          console.error("Error checking Face ID status:", err);
        }
      } else {
        setHasFaceId(false);
      }
    };

    const timeout = setTimeout(checkFaceIdStatus, 500);
    return () => clearTimeout(timeout);
  }, [email, apiUrl, publicAnonKey, usesSupabaseAuth]);

  // Handle WebAuthn Conditional UI (Auto-biometrics on tap)
  useEffect(() => {
    if (!usesSupabaseAuth && isConditionalSupported && step === "email") {
      const startConditionalAuth = async () => {
        try {
          // 1. Get anonymous options (conditional UI doesn't require email upfront)
          const optionsRes = await fetch(
            `${apiUrl}/auth/webauthn/login-options?conditional=true`,
            {
              headers: {
                Authorization: `Bearer ${publicAnonKey}`,
              },
            },
          );

          if (!optionsRes.ok) return;
          const options = await optionsRes.json();
          options.rpId = window.location.hostname;

          // 2. Start authentication with conditional mediation
          // This will wait for the user to select a passkey from the autocomplete list
          const asseResp = await startAuthentication({
            optionsJSON: options,
            useConditionalMediation: true,
          });

          // 3. Verify and login
          setLoading(true);
          const verifyRes = await fetch(
            `${apiUrl}/auth/webauthn/login-verify`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${publicAnonKey}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                body: asseResp,
                appId,
                challenge: options.challenge,
              }),
            },
          );

          const verifyData = await verifyRes.json();
          if (verifyRes.ok && verifyData.token) {
            toast.success(t("login", "welcomeBack", language));
            onLoginSuccess(
              verifyData.token,
              verifyData.email || email,
            );
          }
        } catch (err: any) {
          // Silent failure for conditional UI as it's just a background listener
          if (
            err.name !== "AbortError" &&
            err.name !== "NotAllowedError"
          ) {
            console.error("WebAuthn Conditional Error:", err);
          }
        } finally {
          setLoading(false);
        }
      };

      startConditionalAuth();
    }
  }, [isConditionalSupported, step, usesSupabaseAuth]);

  const handleFaceIdLogin = async () => {
    if (!email) return;
    setLoading(true);
    setError("");

    try {
      // 1. Get options from server
      const optionsRes = await fetch(
        `${apiUrl}/auth/webauthn/login-options?email=${encodeURIComponent(email)}`,
        {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        },
      );

      if (!optionsRes.ok) {
        const err = await optionsRes.json();
        throw new Error(
          err.error || "Erro ao obter opções de Face ID",
        );
      }

      const options = await optionsRes.json();

      // Detect current domain (RP ID)
      const rpID = window.location.hostname;
      options.rpId = rpID;

      // 2. Start authentication
      const asseResp = await startAuthentication({
        optionsJSON: options,
      });

      // 3. Verify with server
      const verifyRes = await fetch(
        `${apiUrl}/auth/webauthn/login-verify`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            body: asseResp,
            appId,
            challenge: options.challenge,
          }),
        },
      );

      const verifyData = await verifyRes.json();

      if (verifyRes.ok && verifyData.token) {
        toast.success(t("login", "welcomeBack", language));
        onLoginSuccess(verifyData.token, email);
      } else {
        setError(
          verifyData.error ||
            "Falha na autenticação biométrica",
        );
      }
    } catch (err: any) {
      console.error(err);
      if (err.name !== "NotAllowedError") {
        setError("Face ID cancelado ou não configurado.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFaceIdRegister = async () => {
    if (!email) return;
    setLoading(true);
    try {
      // Detect current domain (RP ID)
      const currentHost = window.location.hostname;
      // WebAuthn RP ID must be a registrable domain (suffix of hostname)
      // For www.aqlapps.com, it should be aqlapps.com or the full hostname
      const rpID = currentHost;

      // 1. Get options
      const optionsRes = await fetch(
        `${apiUrl}/auth/webauthn/register-options?email=${encodeURIComponent(email)}&rpID=${encodeURIComponent(rpID)}`,
        {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        },
      );

      const options = await optionsRes.json();

      if (!optionsRes.ok) {
        throw new Error(
          options.error || "Erro ao configurar Face ID",
        );
      }

      // 2. Browser interaction
      console.log(
        `🚀 WebAuthn: Starting registration for domain ${rpID}...`,
        options,
      );

      const regResp = await startRegistration({
        optionsJSON: options,
      });
      console.log(
        "✅ WebAuthn: Registration response received",
        regResp,
      );

      // 3. Verify
      const verifyRes = await fetch(
        `${apiUrl}/auth/webauthn/register-verify`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, body: regResp }),
        },
      );

      const verifyData = await verifyRes.json();

      if (verifyRes.ok) {
        toast.success(
          "Face ID ativado! No próximo acesso podes entrar com biometria.",
        );
        setHasFaceId(true);
      } else {
        throw new Error(
          verifyData.error ||
            "Erro ao verificar registo de Face ID",
        );
      }
    } catch (err: any) {
      console.error("❌ WebAuthn Error:", err);
      // Don't show toast for user cancellation
      if (err.name !== "NotAllowedError") {
        setError(
          err.message ||
            "Erro ao configurar biometria. Tenta novamente.",
        );
        toast.error(
          err.message || "Erro ao configurar biometria.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const getBackgroundColors = () => {
    const colorMap: Record<
      string,
      { bg: string; orb1: string; orb2: string; orb3: string }
    > = {
      "from-blue-500 to-blue-600": {
        bg: "from-blue-50 via-cyan-50 to-sky-50",
        orb1: "from-blue-400/30 to-cyan-400/30",
        orb2: "from-cyan-400/30 to-sky-400/30",
        orb3: "from-sky-400/20 to-blue-400/20",
      },
      "from-orange-500 to-orange-600": {
        bg: "from-orange-50 via-amber-50 to-yellow-50",
        orb1: "from-orange-400/30 to-amber-400/30",
        orb2: "from-amber-400/30 to-yellow-400/30",
        orb3: "from-yellow-400/20 to-orange-400/20",
      },
      "from-slate-500 to-slate-600": {
        bg: "from-slate-50 via-gray-50 to-zinc-50",
        orb1: "from-slate-400/30 to-gray-400/30",
        orb2: "from-gray-400/30 to-zinc-400/30",
        orb3: "from-zinc-400/20 to-slate-400/20",
      },
      "from-purple-500 to-purple-600": {
        bg: "from-purple-50 via-violet-50 to-fuchsia-50",
        orb1: "from-purple-400/30 to-violet-400/30",
        orb2: "from-violet-400/30 to-fuchsia-400/30",
        orb3: "from-fuchsia-400/20 to-purple-400/20",
      },
      "from-emerald-500 to-emerald-600": {
        bg: "from-emerald-50 via-green-50 to-teal-50",
        orb1: "from-emerald-400/30 to-green-400/30",
        orb2: "from-green-400/30 to-teal-400/30",
        orb3: "from-teal-400/20 to-emerald-400/20",
      },
      "from-teal-500 to-teal-600": {
        bg: "from-teal-50 via-cyan-50 to-blue-50",
        orb1: "from-teal-400/30 to-cyan-400/30",
        orb2: "from-cyan-400/30 to-blue-400/30",
        orb3: "from-blue-400/20 to-teal-400/20",
      },
      "from-slate-700 to-slate-900": {
        bg: "from-slate-50 via-zinc-100 to-slate-200",
        orb1: "from-[#FFC800]/20 to-amber-400/20",
        orb2: "from-slate-400/20 to-zinc-500/20",
        orb3: "from-amber-300/10 to-[#FFC800]/10",
      },
      "from-slate-900 to-slate-800": { // AQL Barber specific background
        bg: "from-blue-50 via-slate-50 to-white",
        orb1: "from-blue-500/10 to-blue-400/10",
        orb2: "from-blue-600/10 to-slate-400/10",
        orb3: "from-slate-300/10 to-blue-500/10",
      },
      default: {
        bg: "from-indigo-50 via-purple-50 to-pink-50",
        orb1: "from-purple-400/30 to-pink-400/30",
        orb2: "from-blue-400/30 to-cyan-400/30",
        orb3: "from-indigo-400/20 to-purple-400/20",
      },
    };

    return colorMap[appGradient] || colorMap["default"];
  };

  const bgColors = getBackgroundColors();

  const isSBETheme =
    appGradient === "from-slate-700 to-slate-900";
  const isBarber = appId === "aql-barber";
  const isVisionLogin = appId === "vision-alerts";
  const isVisionDarkTheme = theme === "dark-industrial";
  const isVisionLightTheme = theme === "light";
  const headerAppIcon = isVisionLogin ? <AqlVisionMark className="h-full w-full object-contain" /> : appIcon;
  const showGlobalAdminButton = isVisionLogin && needsClientStep;
  const isBlueTheme = isVisionLogin || isBarber || isSBETheme;
  const visionLoginThemeClass = isVisionLogin ? `aql-login-theme-${theme}` : "";
  const visionLabelClass = isVisionLogin
    ? isVisionLightTheme ? "text-slate-700" : "text-slate-200"
    : "text-gray-700";
  const visionPrimaryButtonClass = isVisionLogin
    ? "bg-cyan-400 text-[#06111F] hover:bg-cyan-300 shadow-lg shadow-cyan-400/20"
    : isBlueTheme
      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20"
      : `bg-gradient-to-r ${appGradient} text-white`;
  const visionInputFocusClass = isVisionLogin
    ? "focus:ring-cyan-300/20 focus:border-cyan-300"
    : isBlueTheme
      ? "focus:ring-blue-500/20 focus:border-blue-600"
      : "focus:ring-purple-500/20 focus:border-purple-500";
  const visionInteractiveAccentClass = isVisionLogin
    ? "text-cyan-200 hover:text-cyan-100"
    : "text-blue-400 hover:text-blue-300";
  const visionInputSurfaceClass = isVisionLogin
    ? `aql-login-input border-slate-700 bg-slate-800/80 text-slate-100 placeholder:text-slate-400 caret-slate-100 ${isVisionDarkTheme ? "[color-scheme:dark]" : "[color-scheme:light]"} [&:-webkit-autofill]:[-webkit-text-fill-color:#e2e8f0] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(30_41_59)]`
    : "border-gray-100 bg-gray-50 text-slate-900 placeholder:text-slate-400 caret-slate-900 [color-scheme:light] [&:-webkit-autofill]:[-webkit-text-fill-color:#0f172a] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(248_250_252)]";
  const visionStepTitleClass = "aql-login-step-title text-center text-3xl font-black tracking-tight text-slate-100 sm:text-4xl";
  const visionStepSubtitleClass = "aql-login-step-subtitle mx-auto max-w-xl text-center text-sm font-medium leading-6 text-slate-300 sm:text-base";
  const stepMotionClass = isVisionLogin
    ? ""
    : `animate-in fade-in duration-500 fill-mode-both ${slideDir === "right" ? "slide-in-from-right-8" : "slide-in-from-left-8"}`;
  const profileToneForRole = (role: string) => {
    const normalizedRole = role.toLowerCase();
    if (normalizedRole.includes("admin")) {
      return {
        accent: "#fb7185",
        accentSoft: "rgba(251, 113, 133, 0.12)",
        label: "text-rose-200/85",
        buttonClass: "bg-[#fb7185] text-[#18070d] hover:bg-[#fda4af] shadow-lg shadow-rose-400/20",
        inputFocusClass: "focus:ring-rose-300/20 focus:border-rose-300",
        iconClass: "text-rose-200/75 group-focus-within:text-rose-200"
      };
    }
    if (normalizedRole.includes("operator") || normalizedRole.includes("operador")) {
      return {
        accent: "#67e8f9",
        accentSoft: "rgba(103, 232, 249, 0.12)",
        label: "text-cyan-100/85",
        buttonClass: "bg-cyan-400 text-[#06111F] hover:bg-cyan-300 shadow-lg shadow-cyan-400/20",
        inputFocusClass: "focus:ring-cyan-300/20 focus:border-cyan-300",
        iconClass: "text-cyan-200/75 group-focus-within:text-cyan-100"
      };
    }
    return {
      accent: "#93c5fd",
      accentSoft: "rgba(147, 197, 253, 0.10)",
      label: "text-sky-100/85",
      buttonClass: "bg-sky-300 text-[#07111f] hover:bg-sky-200 shadow-lg shadow-sky-400/18",
      inputFocusClass: "focus:ring-sky-300/20 focus:border-sky-300",
      iconClass: "text-sky-200/75 group-focus-within:text-sky-100"
    };
  };
  const selectedProfileTone = selectedProfile ? profileToneForRole(selectedProfile.role) : null;
  const showProfileCards = usesSupabaseAuth && !fixedEmail && !needsClientStep && loginProfiles.length > 0;
  const profilePickerOpen = isVisionLogin && step === "email" && showProfileCards;
  const profileStepLoading = isVisionLogin && step === "email" && !needsClientStep && profilesLoading;

  useEffect(() => {
    if (!profilePickerOpen) return;
    const clearBrowserCredentialFocus = () => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
      profilePickerRef.current?.focus({ preventScroll: true });
    };

    clearBrowserCredentialFocus();
    const focusFrame = window.requestAnimationFrame(() => {
      clearBrowserCredentialFocus();
    });
    const focusTimer = window.setTimeout(clearBrowserCredentialFocus, 180);
    const secondFocusTimer = window.setTimeout(clearBrowserCredentialFocus, 420);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.clearTimeout(focusTimer);
      window.clearTimeout(secondFocusTimer);
    };
  }, [profilePickerOpen]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    try {
      if (usesSupabaseAuth) {
        setSlideDir("right");
        setStep("password");
        return;
      }

      const url = `${apiUrl}/auth/smart-login`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, appId }),
      });

      const data = await response.json();

      if (response.ok) {
        const isAQLDomain = email
          .toLowerCase()
          .endsWith("@aqlengineering.com");
        const isArsNorteDomain =
          appId === "aql-tension" &&
          email
            .toLowerCase()
            .endsWith("@arsnorte.min-saude.pt");
        const isBypassEmail = email.toLowerCase() === "giselasilva3@hotmail.com";

        if (
          (data.requiresCode &&
            (isAQLDomain || isArsNorteDomain || isBypassEmail)) ||
          (data.action === "code_verified" && data.requiresPassword)
        ) {
          setSlideDir("right");
          setStep("password");
          toast.success(
            data.message || t("login", "createStrongPassword", language),
          );
          setIsNewUser(true);
        } else if (data.requiresCode) {
          setSlideDir("right");
          setStep("code");
          toast.success(
            t("login", "codeSent", language, { email }),
          );
        } else if (data.action === "password_required") {
          setSlideDir("right");
          setStep("password");
          toast.success(t("login", "welcomeBack", language));
        } else {
          setError(t("common", "error", language));
        }
      } else {
        setError(data.error || t("common", "error", language));
      }
    } catch (err) {
      setError(t("login", "connectionError", language));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelect = (profile: LoginProfile) => {
    setSlideDir("right");
    setPendingMorphFrom(null);
    setPanelFlip(null);
    setSelectedProfile(profile);
    setPassword("");
    setError("");
    setStep("password");
  };

  const handleBackToIdentity = () => {
    setSlideDir("left");
    setStep("email");
    setSelectedProfile(null);
    setPendingMorphFrom(null);
    setPanelFlip(null);
    setPassword("");
    setError("");
  };

  const handleClientSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    const query = normalizeClientKey(clientQuery);
    if (!query) {
      setError("Indica o cliente para continuar.");
      return;
    }

    const matchedClient = availableClients.find((item) => {
      const keys = [item.slug, item.name, item.id].map((value) => normalizeClientKey(String(value ?? "")));
      return keys.includes(query);
    });

    if (!matchedClient) {
      setError("Não encontrei esse cliente. Confirma o nome e tenta novamente.");
      return;
    }

    setClient(matchedClient);
    localStorage.setItem(`aql_login_client_confirmed_${appId}`, "1");
    setClientConfirmed(true);
    setError("");
    setSelectedProfile(null);
    setLoginProfiles([]);
    setSlideDir("right");
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const payload = { email, code, appId };
      const response = await fetch(
        `${apiUrl}/auth/smart-login`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (response.ok) {
        if (data.accessToken || data.token) {
          toast.success(
            t("login", "accountCreated", language, { appName }),
          );
          onLoginSuccess(data.accessToken || data.token, email);
        } else if (
          data.action === "code_verified" &&
          data.requiresPassword
        ) {
          toast.success(t("login", "codeVerified", language));
          setSlideDir("right");
          setStep("password");
        } else {
          setError(t("common", "error", language));
        }
      } else {
        setError(data.error || t("common", "error", language));
      }
    } catch (err) {
      setError(t("login", "connectionError", language));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    try {
      if (usesSupabaseAuth) {
        if (selectedProfile) {
          const response = await fetch(`${apiUrl}/vision-settings/profile-login`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${publicAnonKey}`,
              apikey: publicAnonKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: selectedProfile.id,
              client_id: activeClientId || null,
              password,
            }),
          });
          const data = await response.json().catch(() => ({}));

          if (!response.ok || !data.accessToken) {
            setError(data.error || t("login", "invalidPassword", language));
            return;
          }

          if (data.refreshToken) {
            await supabase.auth.setSession({
              access_token: data.accessToken,
              refresh_token: data.refreshToken,
            });
          }

          toast.success(t("login", "welcomeBack", language));
          onLoginSuccess(data.accessToken, data.email || selectedProfile.name);
          return;
        }

        const { data, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (authError) {
          setError(authError.message || t("login", "invalidPassword", language));
          return;
        }

        const token = data.session?.access_token;
        if (!token) {
          setError(t("login", "connectionError", language));
          return;
        }

        toast.success(t("login", "welcomeBack", language));
        onLoginSuccess(token, data.user?.email || email);
        return;
      }

      if (appId === "aql-clock" && !code) {
        const employeeResponse = await fetch(
          `${apiUrl}/clock/employee-login`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${publicAnonKey}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          },
        );

        if (employeeResponse.ok) {
          const employeeData = await employeeResponse.json();
          toast.success(
            t("login", "welcomeEmployee", language, {
              name: employeeData.employee?.name,
            }),
          );
          onLoginSuccess(employeeData.token, email);
          setLoading(false);
          return;
        }
      }

      const payload = code
        ? { email, code, password, appId }
        : { email, password, appId };

      const response = await fetch(
        `${apiUrl}/auth/smart-login`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (response.ok && (data.accessToken || data.token)) {
        const message =
          code || isNewUser
            ? t("login", "accountCreated", language, {
                appName,
              })
            : t("login", "welcomeBack", language);
        toast.success(message);
        onLoginSuccess(data.accessToken || data.token, email);
      } else {
        setError(
          data.error || t("login", "invalidPassword", language),
        );
      }
    } catch (err) {
      setError(t("login", "connectionError", language));
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (usesSupabaseAuth) {
        const redirectTo = `${window.location.origin}${window.location.pathname}`;
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo,
        });

        if (resetError) {
          setError(resetError.message || t("common", "error", language));
          return;
        }

        toast.success("Enviamos um email para repor a password.");
        setSlideDir("right");
        setStep("password");
        return;
      }

      const response = await fetch(
        `${apiUrl}/auth/request-reset-code`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, appId }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setSlideDir("right");
        setStep("reset-code");
        toast.success(t("login", "resetCodeSent", language));
      } else {
        setError(data.error || t("common", "error", language));
      }
    } catch (err) {
      setError(t("login", "connectionError", language));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(t("login", "passwordsDoNotMatch", language));
      return;
    }
    if (newPassword.length < 6) {
      setError(t("login", "passwordTooShort", language));
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(
        `${apiUrl}/auth/reset-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            code,
            newPassword,
            appId,
          }),
        },
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(
          t("login", "passwordResetSuccess", language),
        );
        setSlideDir("right");
        setStep("password");
        setPassword("");
        setCode("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(data.error || t("common", "error", language));
      }
    } catch (err) {
      setError(t("login", "connectionError", language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col relative overflow-hidden ${isVisionLogin ? `aql-login-shell ${visionLoginThemeClass} bg-slate-50 dark:bg-slate-950` : "bg-gradient-to-br from-gray-50 to-white"}`}>
      {isVisionLogin && (
        <>
          <div className="aql-login-theme-bg absolute inset-0 z-0" />
          
          <div className="relative z-50">
            <UniversalAppHeader
              appName={appName}
              appIcon={headerAppIcon}
              compactHeader={true}
              showBrandBadge={false}
              showClientBadge={false}
              contentContainerClassName="w-full px-4 sm:px-8 lg:px-10"
              onBack={() => {
                setSlideDir("left");
                if (step !== "email") {
                  setStep("email");
                  setSelectedProfile(null);
                  setPendingMorphFrom(null);
                  setPanelFlip(null);
                  setPassword("");
                  setError("");
                } else if (showProfileCards) {
                  setSelectedProfile(null);
                  if (requireClientSelection && !fixedEmail) {
                    setLoginProfiles([]);
                    setClientConfirmed(false);
                    localStorage.removeItem(`aql_login_client_confirmed_${appId}`);
                  } else if (onBack) {
                    onBack();
                  } else {
                    window.location.assign("/");
                  }
                } else if (onBack) {
                  onBack();
                } else {
                  window.location.assign("/");
                }
              }}
              rightActions={
                showGlobalAdminButton ? (
                  <button
                    type="button"
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 rounded-lg bg-slate-800/50 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-slate-700/50 hover:text-white"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Administrador</span>
                  </button>
                ) : null
              }
            />
          </div>
        </>
      )}

      {!isVisionLogin && (
        <>
          {/* Background Orbs - Simplified and de-saturated */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-slate-100 rounded-full blur-[120px] opacity-60"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gray-100 rounded-full blur-[120px] opacity-60"></div>
          </div>

          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLCAwLCAwLCAwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30"></div>
        </>
      )}

      <div className={`relative flex-1 ${isVisionLogin ? "flex items-center justify-center p-4 pb-28 pt-20" : "min-h-screen flex items-center justify-center p-4"}`}>
        <div className={isVisionLogin ? "flex flex-1 items-center justify-center px-5 py-10 lg:px-10" : ""}>
          <div className={`w-full ${profilePickerOpen ? "max-w-4xl" : isVisionLogin ? "max-w-[500px]" : "max-w-md"} ${!isVisionLogin && !mounted ? "translate-y-4 opacity-0" : "opacity-100"}`}>
            {onBack && !isVisionLogin && (
              <button
                onClick={onBack}
                className="mb-8 flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-all group px-2 py-1"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-bold uppercase tracking-widest text-[10px]">
                  {t("login.backToApps", language)}
                </span>
              </button>
            )}

          <div className="relative z-10">
            {/* Removed the colored glow backdrop */}
            <div
              ref={panelRef}
              className={`relative ${panelFlip?.active ? "overflow-hidden" : ""} ${isVisionLogin ? `mx-auto w-full rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-[0_20px_55px_-36px_rgba(15,23,42,0.45)] sm:p-8 dark:border-white/10 dark:bg-slate-900/88 dark:shadow-[0_24px_60px_-36px_rgba(0,0,0,0.9)] ${profilePickerOpen ? "max-w-4xl" : "max-w-[500px]"}` : "rounded-[2.5rem] border border-gray-100 bg-white p-10 shadow-[0_48px_96px_-24px_rgba(0,0,0,0.16)]"}`}
            >
                {!isVisionLogin && (
                <div className="flex justify-center gap-2 mb-10">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${step === "email" ? `w-8 ${isBlueTheme ? 'bg-blue-600' : 'bg-gradient-to-r ' + appGradient}` : "w-1.5 bg-gray-300"}`}
                  ></div>
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${step === "code" || step === "password" ? `w-8 ${isBlueTheme ? 'bg-blue-600' : 'bg-gradient-to-r ' + appGradient}` : "w-1.5 bg-gray-300"}`}
                  ></div>
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${step === "password" ? `w-8 ${isBlueTheme ? 'bg-blue-600' : 'bg-gradient-to-r ' + appGradient}` : "w-1.5 bg-gray-300"}`}
                  ></div>
                </div>
                )}

              {!isVisionLogin && (
                <div
                  className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl transform hover:scale-110 transition-transform duration-300 animate-float ${isBarber ? 'bg-slate-900' : isSBETheme ? 'bg-blue-600' : 'bg-gradient-to-br ' + appGradient}`}
                >
                  <div className="text-white scale-110">
                    {appIcon}
                  </div>
                </div>
              )}

              {!isVisionLogin && (
                <h1 className="font-bold text-3xl text-center bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
                  {appName}
                </h1>
              )}

              {!isVisionLogin && (
                <p className="text-center text-gray-600 mb-8 flex items-center justify-center gap-2">
                  {step === "email" && (
                      <>
                        <Sparkles className={`w-4 h-4 ${isBlueTheme ? 'text-blue-500' : 'text-purple-500'}`} />
                        <span>
                          {t(
                            "login.enterEmailToContinue",
                            language,
                          )}
                        </span>
                      </>
                  )}
                {step === "code" && (
                  <>
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span>
                      {t("login.checkEmailForCode", language)}
                    </span>
                  </>
                )}
                {step === "password" && (
                  <>
                    <Lock className="w-4 h-4 text-green-500" />
                    <span>
                      {selectedProfile
                        ? `Password de ${selectedProfile.name}`
                        : code || isNewUser
                        ? t(
                            "login.createStrongPassword",
                            language,
                          )
                        : t("login.welcomeBack", language)}
                    </span>
                  </>
                )}
                {step === "reset-request" &&
                  t("login.enterEmailToReset", language)}
                {step === "reset-code" &&
                  t("login.enterResetCodeAndPass", language)}
              </p>
              )}

              {error && (
                <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 animate-shake ${isVisionLogin ? "bg-red-500/10 border border-red-500/30" : "bg-red-50 border border-red-200"}`}>
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isVisionLogin ? "text-red-300" : "text-red-600"}`} />
                  <p className={`text-sm font-medium ${isVisionLogin ? "text-red-200" : "text-red-800"}`}>
                    {error}
                  </p>
                </div>
              )}

              {step === "email" && needsClientStep && (
                <form
                  key="client"
                  onSubmit={handleClientSubmit}
                  autoComplete="off"
                  className="space-y-5"
                >
                  <div className="space-y-3">
                    <h1 className={visionStepTitleClass}>
                      Bem-vindo
                    </h1>
                    <p className={visionStepSubtitleClass}>
                      Utilize o código de acesso fornecido para entrar.
                    </p>
                  </div>

                  <div>
                    <label className={`${isVisionLogin ? "sr-only" : "block"} text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      Cliente
                    </label>
                    <input
                      type="search"
                      value={clientQuery}
                      onChange={(event) => setClientQuery(event.target.value)}
                      placeholder="AQLVISION_AC...."
                      required
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="off"
                      name="aql_vision_client_lookup"
                      spellCheck={false}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-4 ${visionInputFocusClass} transition-all ${visionInputSurfaceClass}`}
                    />
                  </div>

                  <button
                    type="submit"
                    className={`flex w-full items-center justify-center gap-2 rounded-xl py-4 font-semibold ${visionPrimaryButtonClass}`}
                  >
                    <span>Continuar</span>
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </button>
                </form>
              )}

              {step === "email" && !needsClientStep && showProfileCards && (
                <div
                  key="profiles"
                  ref={profilePickerRef}
                  tabIndex={-1}
                  className="space-y-8 outline-none"
                >
                  <div className="space-y-3">
                    <h1 className={visionStepTitleClass}>
                      Escolha o utilizador
                    </h1>
                    <p className={visionStepSubtitleClass}>
                      {client?.name
                        ? `Escolha o perfil de utilizador para aceder aos projetos ${client.name}.`
                        : "Selecione o utilizador para continuar."}
                    </p>
                  </div>

                  <div className="mx-auto grid max-w-3xl grid-cols-[repeat(auto-fit,minmax(12rem,13rem))] justify-center gap-4">
                    {loginProfiles.map((profile) => {
                      const tone = profileToneForRole(profile.role);
                      return (
                        <button
                          key={profile.id}
                          type="button"
                          onClick={() => handleProfileSelect(profile)}
                          className="aql-login-profile-card group relative h-28 w-full p-5 text-center focus:outline-none focus:ring-2 focus:ring-cyan-400/35"
                          style={{
                            ["--profile-accent" as string]: tone.accent,
                            ["--profile-accent-soft" as string]: tone.accentSoft
                          }}
                        >
                          <span className="aql-login-profile-rail pointer-events-none absolute inset-y-0 left-0 w-1" />
                          <span className="aql-login-profile-scan pointer-events-none absolute inset-x-4 top-0 h-px" />
                          <div className="relative flex h-full min-w-0 flex-col items-start justify-center text-left">
                            <span className="block max-w-full text-base font-bold leading-snug text-slate-50 drop-shadow-[0_1px_10px_rgba(226,232,240,0.18)]">
                              {profile.name}
                            </span>
                            <span className={`aql-login-role-label aql-lab-mono mt-2 block max-w-full truncate text-[10px] font-semibold uppercase tracking-[0.18em] ${tone.label}`}>
                              {profile.roleLabel}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {profileStepLoading && (
                <div key="profile-loading" className={stepMotionClass}>
                  <div className="rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-4 text-center text-sm font-medium text-slate-300">
                    A procurar perfis do cliente...
                  </div>
                </div>
              )}

              {step === "email" && !needsClientStep && !showProfileCards && !profileStepLoading && (
                <form
                  onSubmit={handleEmailSubmit}
                  autoComplete={isVisionLogin ? "off" : "on"}
                  className={`space-y-6 ${stepMotionClass}`}
                >
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      {t("login.emailAddress", language)}
                    </label>
                    <div className="relative group">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isVisionLogin ? "text-slate-400 group-focus-within:text-blue-400" : `text-gray-400 ${isBlueTheme ? 'group-focus-within:text-blue-600' : 'group-focus-within:text-purple-500'}`} transition-colors`} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) =>
                          setEmail(e.target.value)
                        }
                        placeholder={t(
                          "login.emailPlaceholder",
                          language,
                        )}
                        required
                        autoFocus={!isVisionLogin}
                        autoComplete={isVisionLogin ? "new-password" : "username webauthn"}
                        name={isVisionLogin ? "aql_vision_login_identifier" : "email"}
                        spellCheck={false}
                        data-lpignore={isVisionLogin ? "true" : undefined}
                        data-1p-ignore={isVisionLogin ? "true" : undefined}
                        className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:ring-4 ${visionInputFocusClass} transition-all ${visionInputSurfaceClass}`}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full py-4 ${visionPrimaryButtonClass} rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                    >
                      {loading && !hasFaceId ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>
                            {t("common.loading", language)}
                          </span>
                        </>
                      ) : (
                        <>
                          <span>
                            {t("login.continue", language)}
                          </span>
                          <ArrowLeft className="w-5 h-5 rotate-180" />
                        </>
                      )}
                    </button>

                    {hasFaceId && (
                      <button
                        type="button"
                        onClick={handleFaceIdLogin}
                        disabled={loading}
                        className={`w-full py-4 border-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-sm ${isVisionLogin ? "bg-slate-800/80 border-slate-700 text-slate-100 hover:bg-slate-800 hover:border-slate-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"}`}
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Fingerprint className="w-5 h-5 text-blue-500" />
                        )}
                        <span>Entrar com Face ID</span>
                      </button>
                    )}

                    {isVisionLogin && (
                      profilesLoading ? (
                        <div className="rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-center text-sm text-slate-300">
                          A procurar perfis do cliente...
                        </div>
                      ) : null
                    )}

                  </div>
                </form>
              )}

              {step === "code" && (
                <form
                  onSubmit={handleCodeSubmit}
                  className={`space-y-6 ${stepMotionClass}`}
                >
                  <div>
                    <label className={`block text-sm font-semibold mb-3 text-center ${visionLabelClass}`}>
                      {t("login", "sixDigitCode", language)}
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) =>
                        setCode(
                          e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6),
                        )
                      }
                      placeholder="000000"
                      required
                      autoFocus
                      maxLength={6}
                      className={`w-full px-4 py-6 border-2 rounded-xl text-center text-4xl tracking-[0.5em] font-bold transition-all ${isVisionLogin ? "bg-slate-800/80 text-slate-100 caret-slate-100 [color-scheme:dark] [&:-webkit-autofill]:[-webkit-text-fill-color:#e2e8f0] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(30_41_59)]" : "bg-gray-50 text-slate-900 caret-slate-900 [color-scheme:light] [&:-webkit-autofill]:[-webkit-text-fill-color:#0f172a] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(248_250_252)]"} ${
                        code.length === 6
                          ? "border-green-500 ring-4 ring-green-500/20"
                          : `${isVisionLogin ? "border-slate-700 focus:ring-slate-400/20 focus:border-slate-500" : "border-gray-100 focus:ring-purple-500/20 focus:border-purple-500"}`
                      }`}
                    />
                    {code.length === 6 && (
                      <div className="mt-3 flex items-center justify-center gap-2 text-green-600 animate-bounce-in">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-semibold">
                          {t("login", "codeComplete", language)}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className={`w-full py-4 ${visionPrimaryButtonClass} rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>
                          {t("common", "loading", language)}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>
                          {t(
                            "login",
                            "verifyAndContinue",
                            language,
                          )}
                        </span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSlideDir("left");
                      setStep("email");
                    }}
                    className={`w-full py-3 transition-colors font-medium rounded-lg ${isVisionLogin ? "text-slate-300 hover:text-slate-100 hover:bg-slate-800/80" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}
                  >
                    ← {t("login", "backToEmail", language)}
                  </button>
                </form>
              )}

              {step === "password" && (
                <form
                  onSubmit={handlePasswordSubmit}
                  className={`space-y-6 ${stepMotionClass}`}
                  style={selectedProfileTone ? ({
                    ["--profile-accent" as string]: selectedProfileTone.accent,
                    ["--profile-accent-soft" as string]: selectedProfileTone.accentSoft
                  }) : undefined}
                >
                  {selectedProfile ? (
                    <div className={isVisionLogin ? "space-y-3 text-center" : "space-y-1 text-left"}>
                      {isVisionLogin ? <h1 className={visionStepTitleClass}>Entrar</h1> : null}
                      <p className={isVisionLogin ? "text-lg font-semibold text-slate-200" : "text-2xl font-semibold leading-tight text-slate-900"}>
                        {selectedProfile.name}
                      </p>
                      <p className={isVisionLogin ? `aql-login-role-label aql-lab-mono text-center text-xs font-semibold uppercase tracking-[0.18em] ${selectedProfileTone?.label ?? "text-cyan-200/70"}` : "aql-lab-mono text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"}>
                        {selectedProfile.roleLabel}
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      {t("login", "password", language)}
                    </label>
                    <div className="relative group">
                      <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isVisionLogin ? selectedProfileTone?.iconClass ?? "text-cyan-200/75 group-focus-within:text-cyan-100" : `text-gray-400 ${isBlueTheme ? 'group-focus-within:text-blue-600' : 'group-focus-within:text-purple-500'}`} transition-colors`} />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) =>
                          setPassword(e.target.value)
                        }
                        placeholder={t(
                          "login",
                          "password",
                          language,
                        )}
                        required
                        autoFocus
                        className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl ${isVisionLogin ? "aql-login-password-input" : `focus:ring-4 ${visionInputFocusClass}`} transition-all ${visionInputSurfaceClass}`}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 ${isVisionLogin ? selectedProfileTone?.buttonClass ?? visionPrimaryButtonClass : visionPrimaryButtonClass} rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>
                          {code || isNewUser
                            ? t(
                                "login",
                                "creatingAccount",
                                language,
                              )
                            : t("login", "signingIn", language)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          {code || isNewUser
                            ? t(
                                "login",
                                "createAccount",
                                language,
                              )
                            : t("login", "signIn", language)}
                        </span>
                        {!code && !isNewUser && (
                          <ArrowLeft className="w-5 h-5 rotate-180" />
                        )}
                      </>
                    )}
                  </button>

                  <div className="flex flex-col gap-4 pt-2">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={handleBackToIdentity}
                        className={`aql-login-secondary-action text-sm transition-colors font-medium ${isVisionLogin ? "text-slate-300 hover:text-slate-100" : "text-gray-600 hover:text-gray-900"}`}
                      >
                        ←{" "}
                        {selectedProfile ? "Escolher outro perfil" : t("login", "differentEmail", language)}
                      </button>
                      {!selectedProfile && (
                        <button
                          type="button"
                          onClick={() => {
                            setSlideDir("right");
                            setStep("reset-request");
                          }}
                          className={`text-sm ${isVisionLogin ? visionInteractiveAccentClass : isBlueTheme ? 'text-blue-600' : 'bg-gradient-to-r ' + appGradient + ' bg-clip-text text-transparent'} hover:opacity-80 transition-opacity font-semibold`}
                        >
                          {t("login", "forgotPassword", language)}
                        </button>
                      )}
                    </div>

                    {!usesSupabaseAuth && !hasFaceId && isBiometricsSupported && (
                      <button
                        type="button"
                        onClick={handleFaceIdRegister}
                        disabled={loading}
                        className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-dashed transition-all text-sm group ${isVisionLogin ? "border-slate-700 text-slate-300 hover:text-slate-100 hover:border-slate-500 hover:bg-slate-800/70" : "border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 hover:bg-slate-50"}`}
                      >
                        <ShieldCheck className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                        <span>Ativar Face ID / Biometria</span>
                      </button>
                    )}
                  </div>
                </form>
              )}

              {step === "reset-request" && (
                <form
                  onSubmit={handleResetRequest}
                  className={`space-y-6 ${stepMotionClass}`}
                >
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      {t("login", "emailAddress", language)}
                    </label>
                    <div className="relative group">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${isVisionLogin ? "text-slate-400 group-focus-within:text-blue-400" : "text-gray-400 group-focus-within:text-purple-500"}`} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) =>
                          setEmail(e.target.value)
                        }
                        placeholder={t(
                          "login",
                          "emailPlaceholder",
                          language,
                        )}
                        required
                        autoFocus
                        className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl transition-all backdrop-blur-sm ${isVisionLogin ? "border-slate-700 bg-slate-800/80 text-slate-100 placeholder:text-slate-400 caret-slate-100 [color-scheme:dark] [&:-webkit-autofill]:[-webkit-text-fill-color:#e2e8f0] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(30_41_59)] focus:ring-slate-400/20 focus:border-slate-500" : "border-gray-200 bg-white/50 text-slate-900 placeholder:text-slate-400 caret-slate-900 [color-scheme:light] [&:-webkit-autofill]:[-webkit-text-fill-color:#0f172a] [&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_rgb(255_255_255)] focus:ring-purple-500/20 focus:border-purple-500"}`}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 ${visionPrimaryButtonClass} rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>
                          {t("common", "loading", language)}
                        </span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        <span>
                          {t(
                            "login",
                            "sendResetCode",
                            language,
                          )}
                        </span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSlideDir("left");
                      setStep("password");
                    }}
                    className={`w-full py-3 transition-colors font-medium rounded-lg ${isVisionLogin ? "text-slate-300 hover:text-slate-100 hover:bg-slate-800/80" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}
                  >
                    ← {t("login", "backToLogin", language)}
                  </button>
                </form>
              )}

              {step === "reset-code" && (
                <form
                  onSubmit={handleResetSubmit}
                  className={`space-y-6 ${stepMotionClass}`}
                >
                  <div>
                    <label className={`block text-sm font-semibold mb-2 text-center ${visionLabelClass}`}>
                      {t("login", "verificationCode", language)}
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) =>
                        setCode(
                          e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6),
                        )
                      }
                      placeholder="000000"
                      required
                      autoFocus
                      className={`w-full px-4 py-4 border-2 rounded-xl text-center text-2xl font-bold backdrop-blur-sm ${isVisionLogin ? "border-slate-700 bg-slate-800/80 text-slate-100 focus:ring-slate-400/20 focus:border-slate-500" : "border-gray-200 bg-white/50 focus:ring-purple-500/20 focus:border-purple-500"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      {t("login", "password", language)}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) =>
                        setNewPassword(e.target.value)
                      }
                      placeholder={t(
                        "login",
                        "password",
                        language,
                      )}
                      required
                      className={`w-full px-4 py-4 border-2 rounded-xl backdrop-blur-sm ${isVisionLogin ? "border-slate-700 bg-slate-800/80 text-slate-100 placeholder:text-slate-400 focus:ring-slate-400/20 focus:border-slate-500" : "border-gray-200 bg-white/50 focus:ring-purple-500/20 focus:border-purple-500"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${visionLabelClass}`}>
                      {t("common", "save", language)}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) =>
                        setConfirmPassword(e.target.value)
                      }
                      placeholder={t(
                        "login",
                        "password",
                        language,
                      )}
                      required
                      className={`w-full px-4 py-4 border-2 rounded-xl backdrop-blur-sm ${isVisionLogin ? "border-slate-700 bg-slate-800/80 text-slate-100 placeholder:text-slate-400 focus:ring-slate-400/20 focus:border-slate-500" : "border-gray-200 bg-white/50 focus:ring-purple-500/20 focus:border-purple-500"}`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 ${visionPrimaryButtonClass} rounded-xl font-semibold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>
                          {t("common", "loading", language)}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>
                          {t("common", "save", language)}
                        </span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {!isVisionLogin ? (
                <div className={`${profilePickerOpen ? "mt-8 pt-0 border-t-0" : "mt-10 pt-6 border-t"} flex flex-col items-center gap-3 border-gray-100`}>
                  <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase">
                    <span className={isBlueTheme ? "text-blue-600" : "text-purple-600"}>
                      AQL APPS
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="text-gray-400">
                      {t("login.secureAuth", language)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        </div>

        {isVisionLogin ? (
          <footer className="pointer-events-auto absolute inset-x-0 bottom-6 flex flex-col items-center gap-2.5 px-4">
            <a 
              href="https://aqlengineering.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block transition-transform hover:scale-105 active:scale-95"
            >
              <AqlSeahorseLogo
                alt="AQL Engineering"
                className="h-9 w-9 rounded-full border border-slate-700/50 bg-slate-900 object-cover p-1 shadow-lg"
              />
            </a>
            <p className="text-[10px] font-medium tracking-[0.18em] text-slate-400">AQL Engineering, lda.</p>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
